import { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/Brand";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("signup") ? "register" : "login");
  const [form, setForm] = useState({ clinic_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");

  if (user) return <Navigate to="/" replace />;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setInfo(data.message);
        return;
      }
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand side */}
      <div className="hidden lg:flex flex-col justify-between bg-ink text-cream p-14 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{ background: "radial-gradient(circle at 30% 20%, #C5A059 0%, transparent 45%)" }}
        />
        <div className="relative z-10">
          <BrandLogo className="h-14" variant="dark" testid="login-brand-logo" />
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-5xl font-extrabold leading-[1.05] tracking-tight">
            A sala de espera da sua clínica, chamada com um clique.
          </h1>
          <p className="text-cream/70 text-lg max-w-md leading-relaxed">
            O nome do paciente aparece na TV e a voz chama junto. Simples, rápido e com a cara da sua clínica.
          </p>
        </div>
        <div className="relative z-10 flex gap-8 text-sm text-cream/60">
          <div><span className="block text-2xl font-heading font-bold text-gold">1 clique</span>da fila à TV</div>
          <div><span className="block text-2xl font-heading font-bold text-gold">Voz</span>chamada falada</div>
          <div><span className="block text-2xl font-heading font-bold text-gold">Qualquer TV</span>com navegador</div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-cream">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden mb-8">
            <BrandLogo className="h-12" testid="login-brand-logo-mobile" />
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">
            {mode === "login" ? "Bem-vindo de volta" : mode === "forgot" ? "Recuperar senha" : "Criar conta da clínica"}
          </h2>
          <p className="text-cappuccino mb-8">
            {mode === "login" ? "Acesse o painel de chamada." : mode === "forgot" ? "Informe seu e-mail e enviaremos um link para criar uma nova senha." : "Em menos de um minuto você chama o primeiro paciente."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="clinic_name">Nome da clínica</Label>
                  <Input id="clinic_name" data-testid="clinic-name-input" value={form.clinic_name}
                    onChange={upd("clinic_name")} required placeholder="Clínica Exemplo" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="name">Seu nome</Label>
                  <Input id="name" data-testid="name-input" value={form.name}
                    onChange={upd("name")} required placeholder="Dra. Helena" className="mt-1.5" />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" data-testid="email-input" type="email" value={form.email}
                onChange={upd("email")} required placeholder="voce@clinica.com" className="mt-1.5" />
            </div>
            {mode !== "forgot" && (
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                {mode === "login" && (
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                    className="text-xs text-gold font-semibold hover:underline">
                    Esqueci minha senha
                  </button>
                )}
              </div>
              <Input id="password" data-testid="password-input" type="password" value={form.password}
                onChange={upd("password")} required placeholder="••••••••" className="mt-1.5" />
            </div>
            )}

            {info && (
              <p data-testid="auth-info" className="text-sm text-ink bg-accent rounded-md px-3 py-2">{info}</p>
            )}

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button type="submit" data-testid="auth-submit" disabled={busy}
              className="w-full h-12 rounded-md bg-gold hover:bg-gold-hover text-white font-semibold text-base transition-colors group">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <span className="flex items-center gap-2">
                  {mode === "login" ? "Entrar" : mode === "forgot" ? "Enviar link de recuperação" : "Criar conta"}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              )}
            </Button>
          </form>

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-5 text-xs text-cappuccino">
                <span className="h-px flex-1 bg-border" /> ou <span className="h-px flex-1 bg-border" />
              </div>
              <button type="button" data-testid="google-login-button" onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
                className="w-full h-12 rounded-md border border-border bg-white hover:bg-accent text-ink font-semibold text-sm inline-flex items-center justify-center gap-3 transition-colors">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
                {mode === "login" ? "Entrar com Google" : "Criar conta com Google"}
              </button>
            </>
          )}
          {mode === "forgot" ? (
            <p className="text-center text-sm text-cappuccino mt-6">
              Lembrou a senha?{" "}
              <button data-testid="back-to-login" onClick={() => { setMode("login"); setError(""); setInfo(""); }}
                className="text-gold font-semibold hover:underline">Voltar para o login</button>
            </p>
          ) : (
          <p className="text-center text-sm text-cappuccino mt-6">
            {mode === "login" ? "Ainda não tem conta?" : "Já tem conta?"}{" "}
            <button data-testid="toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setInfo(""); }}
              className="text-gold font-semibold hover:underline">
              {mode === "login" ? "Criar conta" : "Entrar"}
            </button>
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
